import React, { useState, useRef } from 'react';
import { useAuth, api } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_MB = 5;

export default function ProfilePage() {
  const { user, fetchUser, logout } = useAuth();
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileInputRef = useRef(null);

  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ first_name: '', last_name: '', email: '', current_password: '' });

  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Formato non supportato. Usa JPEG, PNG o WebP.');
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`File troppo grande. Massimo ${MAX_MB} MB.`);
      return;
    }

    setPreview(URL.createObjectURL(file));
    handleUpload(file);
  };

  const handleUpload = async (file) => {
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post('/upload/user/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await fetchUser();
      toast.success('Avatar aggiornato!');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Errore durante il caricamento.';
      toast.error(msg);
      setPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const openEdit = () => {
    setFormData({ first_name: user?.first_name || '', last_name: user?.last_name || '', email: user?.email || '', current_password: '' });
    setEditMode(true);
  };

  const emailChanged = formData.email !== user?.email;

  const handleSave = async () => {
    if (emailChanged && !formData.current_password) {
      toast.error('Inserisci la password per cambiare email.');
      return;
    }
    setSaving(true);
    try {
      await api.put('/auth/me', { first_name: formData.first_name, last_name: formData.last_name });
      if (emailChanged) {
        await api.put('/auth/me/email', { new_email: formData.email, current_password: formData.current_password });
        toast.success('Email aggiornata! Controlla la tua casella per verificarla.');
      } else {
        toast.success('Dati aggiornati!');
      }
      await fetchUser();
      setEditMode(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore durante il salvataggio.');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get('/auth/me/export');
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dalsud-dati-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Errore durante l\'esportazione.');
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      toast.error('Inserisci la password per confermare.');
      return;
    }
    setDeleting(true);
    try {
      await api.delete('/auth/me', { data: { current_password: deletePassword } });
      logout();
      navigate('/');
      toast.success('Account eliminato. Arrivederci.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore durante la cancellazione.');
    } finally {
      setDeleting(false);
    }
  };

  const avatarSrc = preview || user?.avatar_url;
  const initials = [user?.first_name, user?.last_name]
    .filter(Boolean)
    .map((n) => n[0].toUpperCase())
    .join('') || user?.email?.[0]?.toUpperCase() || '?';

  return (
    <div className="max-w-2xl mx-auto px-4 py-10">
      <h1 className="text-2xl font-bold text-text-primary mb-8">Il mio account</h1>

      {/* Avatar */}
      <div className="bg-white rounded-xl border border-border p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Foto profilo</h2>
        <div className="flex items-center gap-6">
          <div className="relative w-20 h-20 shrink-0">
            {avatarSrc ? (
              <img
                src={avatarSrc}
                alt="Avatar"
                className="w-20 h-20 rounded-full object-cover border border-border"
              />
            ) : (
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center text-primary text-2xl font-bold border border-border">
                {initials}
              </div>
            )}
            {uploading && (
              <div className="absolute inset-0 rounded-full bg-black/30 flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>
          <div>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              {uploading ? 'Caricamento...' : 'Cambia foto'}
            </button>
            <p className="text-xs text-text-secondary mt-2">
              JPEG, PNG o WebP — max {MAX_MB} MB
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>
        </div>
      </div>

      {/* Dati account */}
      <div className="bg-white rounded-xl border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Dati account</h2>
          {!editMode && (
            <button
              onClick={openEdit}
              className="text-sm text-primary hover:underline font-medium"
            >
              Modifica
            </button>
          )}
        </div>

        {editMode ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-text-secondary mb-1">Nome</label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData(p => ({ ...p, first_name: e.target.value }))}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">Cognome</label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData(p => ({ ...p, last_name: e.target.value }))}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-text-secondary mb-1">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(p => ({ ...p, email: e.target.value, current_password: '' }))}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
            {emailChanged && (
              <div>
                <label className="block text-xs text-text-secondary mb-1">Password attuale <span className="text-red-500">*</span></label>
                <input
                  type="password"
                  value={formData.current_password}
                  onChange={(e) => setFormData(p => ({ ...p, current_password: e.target.value }))}
                  placeholder="Richiesta per cambiare email"
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
            )}
            <div className="flex gap-3 pt-1">
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Salvataggio...' : 'Salva'}
              </button>
              <button
                onClick={() => setEditMode(false)}
                disabled={saving}
                className="px-4 py-2 border border-border rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Annulla
              </button>
            </div>
          </div>
        ) : (
          <dl className="space-y-3 text-sm">
            <div className="flex justify-between">
              <dt className="text-text-secondary">Nome</dt>
              <dd className="font-medium">
                {[user?.first_name, user?.last_name].filter(Boolean).join(' ') || '—'}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-text-secondary">Email</dt>
              <dd className="font-medium">{user?.email}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-text-secondary">Ruolo</dt>
              <dd className="font-medium capitalize">{user?.role?.replace('_', ' ')}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-text-secondary">Email verificata</dt>
              <dd className={user?.email_verified ? 'text-green-600 font-medium' : 'text-amber-600 font-medium'}>
                {user?.email_verified ? 'Sì' : 'No'}
              </dd>
            </div>
          </dl>
        )}
      </div>
      {/* Zona pericolosa */}
      <div className="bg-white rounded-xl border border-red-200 p-6 mt-6">
        <h2 className="text-lg font-semibold text-red-700 mb-1">Zona pericolosa</h2>
        <p className="text-xs text-text-secondary mb-4">
          Queste azioni sono irreversibili. I tuoi dati personali verranno eliminati in conformità al GDPR (art. 17).
        </p>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleExport}
            disabled={exporting}
            className="px-4 py-2 border border-border rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            {exporting ? 'Esportazione...' : 'Scarica i miei dati'}
          </button>
          <button
            onClick={() => { setDeleteConfirm(true); setDeletePassword(''); }}
            className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
          >
            Elimina account
          </button>
        </div>

        {deleteConfirm && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg space-y-3">
            <p className="text-sm text-red-800 font-medium">
              Sei sicuro? Questa azione è permanente e non può essere annullata.
            </p>
            <div>
              <label className="block text-xs text-text-secondary mb-1">Conferma con la tua password</label>
              <input
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder="La tua password attuale"
                className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-300"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleDeleteAccount}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {deleting ? 'Eliminazione...' : 'Sì, elimina il mio account'}
              </button>
              <button
                onClick={() => setDeleteConfirm(false)}
                disabled={deleting}
                className="px-4 py-2 border border-border rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Annulla
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
